import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import type { CreateInviteLinkData, InviteLink } from '@/types/inviteLink';

export const inviteLinksQueryKey = (tournamentId: number) => ['invite-links', tournamentId] as const;

/** Manager-side invite links of a tournament: list + create + revoke + delete. */
export function useInviteLinks(tournamentId: number) {
  const queryClient = useQueryClient();
  const queryKey = inviteLinksQueryKey(tournamentId);

  const linksQuery = useQuery<InviteLink[]>({
    queryKey,
    queryFn: async () => {
      const response = await apiClient.getInviteLinks(tournamentId);
      return response.data.data;
    },
    staleTime: 0,
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateInviteLinkData) => {
      const response = await apiClient.createInviteLink(tournamentId, data);
      return response.data.data;
    },
    onSuccess: (created) => {
      // Show the new link immediately (active links are listed first), then sync with the server.
      queryClient.setQueryData<InviteLink[]>(queryKey, (prev) => [created, ...(prev ?? [])]);
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (linkId: number) => apiClient.revokeInviteLink(tournamentId, linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (linkId: number) => apiClient.deleteInviteLink(tournamentId, linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return { linksQuery, createMutation, revokeMutation, deleteMutation };
}
